import { useSyncExternalStore } from 'react'
import { ChartSnapshot, SeriesData } from './chart.typings'
import { chartSnapshotChangedKeys } from './chartSnapshotEquality'
import { subscribeChartCrosshairRevision } from './chartCrosshairRevisionStore'

type Listener = () => void

/** Stable empty snapshot — required for useSyncExternalStore referential equality. */
export const EMPTY_CHART_SNAPSHOT: ChartSnapshot = {}

const snapshots: Record<string, ChartSnapshot> = {}
const crosshairSnapshots: Record<string, ChartSnapshot | null | undefined> = {}
const seriesGroupListeners: Record<string, Set<Listener>> = {}

function seriesListenerKey(viewKey: string, seriesGroupId: string) {
    return `${viewKey}:${seriesGroupId}`
}

function notifySeriesGroupListeners(viewKey: string, seriesGroupId: string) {
    seriesGroupListeners[seriesListenerKey(viewKey, seriesGroupId)]?.forEach((listener) => listener())
}

/** Re-run live metadata formatters (e.g. output precision) without a new candle tick. */
export function notifyChartLiveDataForSeriesGroup(viewKey: string, seriesGroupId: string) {
    notifySeriesGroupListeners(viewKey, seriesGroupId)
}

export function setChartSnapshotForView(viewKey: string, snapshot: ChartSnapshot) {
    const prev = snapshots[viewKey]
    const changedKeys = chartSnapshotChangedKeys(prev, snapshot)
    if (changedKeys.length === 0) return

    snapshots[viewKey] = snapshot
    changedKeys.forEach((seriesGroupId) => {
        notifySeriesGroupListeners(viewKey, seriesGroupId)
    })
}

export function setChartCrosshairSnapshotForView(viewKey: string, snapshot: ChartSnapshot | null) {
    crosshairSnapshots[viewKey] = snapshot
}

export function hasChartCrosshairSnapshot(viewKey: string): boolean {
    return crosshairSnapshots[viewKey] != null
}

export function getChartSnapshotForView(viewKey: string): ChartSnapshot {
    return snapshots[viewKey] ?? EMPTY_CHART_SNAPSHOT
}

export function getEffectiveChartSnapshot(viewKey: string): ChartSnapshot {
    const crosshair = crosshairSnapshots[viewKey]
    if (crosshair) return crosshair
    return snapshots[viewKey] ?? EMPTY_CHART_SNAPSHOT
}

export function getChartSnapshotSeriesGroup(viewKey: string, seriesGroupId: string): Record<string, SeriesData[]> | undefined {
    return snapshots[viewKey]?.[seriesGroupId]
}

export function getEffectiveChartSnapshotSeriesGroup(
    viewKey: string,
    seriesGroupId: string
): Record<string, SeriesData[]> | undefined {
    return getEffectiveChartSnapshot(viewKey)[seriesGroupId] as Record<string, SeriesData[]> | undefined
}

export function subscribeChartSnapshotSeriesGroup(viewKey: string, seriesGroupId: string, listener: Listener) {
    const key = seriesListenerKey(viewKey, seriesGroupId)
    if (!seriesGroupListeners[key]) {
        seriesGroupListeners[key] = new Set()
    }
    seriesGroupListeners[key].add(listener)
    return () => {
        seriesGroupListeners[key]?.delete(listener)
    }
}

/** Subscribe to chart snapshot ticks and crosshair moves for live metadata text. */
export function subscribeChartLiveData(viewKey: string, seriesGroupIds: string[], listener: Listener) {
    const uniqueIds = Array.from(new Set(['primary', ...seriesGroupIds]))
    const unsubs = uniqueIds.map((id) => subscribeChartSnapshotSeriesGroup(viewKey, id, listener))
    unsubs.push(subscribeChartCrosshairRevision(viewKey, listener))
    return () => {
        unsubs.forEach((unsub) => unsub())
    }
}

export function useChartSnapshotSeriesGroup(viewKey: string, seriesGroupId: string): Record<string, SeriesData[]> | undefined {
    return useSyncExternalStore(
        (listener) => subscribeChartSnapshotSeriesGroup(viewKey, seriesGroupId, listener),
        () => getChartSnapshotSeriesGroup(viewKey, seriesGroupId),
        () => undefined
    )
}
